import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloseOutlined, StorefrontOutlined } from '@mui/icons-material';
import { slugify } from '../constants';
import { InspSectionTitle, InspRow, InspInput, ToggleRow, selectStyle } from '../ui/primitives';
import { MediaField } from '../MediaField';
import { ExhibitorInspector } from './ExhibitorInspector';

export const EXHIBITION_COLOR = '#0d7a5f';

const BOOTH_SHAPES = [
  { value: 'trapezoid', label: 'Trapezoid', desc: 'Classic angled top edge'  },
  { value: 'rect',      label: 'Rectangle', desc: 'Clean square card'         },
  { value: 'rounded',   label: 'Rounded',   desc: 'Soft rounded corners'      },
];

/**
 * Left panel for the exhibition hall tab.
 * • When an exhibitor card is selected  → shows ExhibitorInspector
 * • Otherwise                           → shows hall-level settings
 *
 * Props:
 *   exhibition          — ExhibitionPage document
 *   onUpdate            — (patch) => void
 *   onClose             — () => void
 *   selectedExhibitor   — Exhibitor | null
 *   onUpdateExhibitor   — (patch) => void
 *   onDeleteExhibitor   — () => void
 *   onEditBooth         — () => void
 *   onDeselectExhibitor — () => void
 */
export function ExhibitionInspector({
  exhibition,
  onUpdate,
  onClose,
  selectedExhibitor,
  onUpdateExhibitor,
  onDeleteExhibitor,
  onEditBooth,
  onDeselectExhibitor,
}) {
  if (!exhibition) return null;

  // ── Exhibitor selected — hand off entirely ─────────────────────────────────
  if (selectedExhibitor) {
    return (
      <ExhibitorInspector
        exhibitor={selectedExhibitor}
        onUpdate={onUpdateExhibitor}
        onDelete={onDeleteExhibitor}
        onEditBooth={onEditBooth}
        onClose={onDeselectExhibitor}
      />
    );
  }

  // ── Hall settings ──────────────────────────────────────────────────────────
  const upd = patch => onUpdate(patch);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.75, borderBottom: '1px solid #e8edf2',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: 1.5,
            background: alpha(EXHIBITION_COLOR, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: EXHIBITION_COLOR,
          }}>
            <StorefrontOutlined sx={{ fontSize: 15 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a2e', lineHeight: 1 }}>
              Hall Settings
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#b0bac8', mt: 0.2 }}>
              {exhibition.name}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3' }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {/* ── General ── */}
        <InspSectionTitle label="General" color={EXHIBITION_COLOR} />

        <InspRow label="Name">
          <InspInput
            value={exhibition.name || ''}
            onChange={v => upd({ name: v })}
            placeholder="e.g. Main Hall"
          />
        </InspRow>

        <InspRow label="Slug">
          <InspInput
            value={exhibition.slug || ''}
            onChange={v => upd({ slug: slugify(v) })}
            placeholder="main-hall"
          />
        </InspRow>

        <ToggleRow
          label="Default hall"
          value={!!exhibition.isDefault}
          onChange={v => upd({ isDefault: v })}
        />
        <ToggleRow
          label="Active"
          value={exhibition.isActive !== false}
          onChange={v => upd({ isActive: v })}
        />

        {/* ── Background ── */}
        <InspSectionTitle label="Background" color="#e67e22" />

        <InspRow label="Type">
          <select
            value={exhibition.bgType || 'color'}
            onChange={e => upd({ bgType: e.target.value })}
            style={selectStyle}
          >
            <option value="color">Solid color</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
        </InspRow>

        {(!exhibition.bgType || exhibition.bgType === 'color') ? (
          <InspRow label="Color">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="color"
                value={exhibition.bgColor || '#D1D9E0'}
                onChange={e => upd({ bgColor: e.target.value })}
                style={{ width: 28, height: 28, border: '1px solid #e4e9f0', borderRadius: 4, padding: 2, cursor: 'pointer', background: 'none' }}
              />
              <InspInput
                value={exhibition.bgColor || '#D1D9E0'}
                onChange={v => upd({ bgColor: v })}
                placeholder="#D1D9E0"
              />
            </Box>
          </InspRow>
        ) : (
          <InspRow label={exhibition.bgType === 'video' ? 'Video' : 'Image'}>
            <MediaField
              value={exhibition.bgUrl || ''}
              onChange={v => upd({ bgUrl: v })}
              mediaType={exhibition.bgType === 'video' ? 'video' : 'image'}
            />
          </InspRow>
        )}

        {/* ── Grid Layout ── */}
        <InspSectionTitle label="Grid Layout" color="#1075BA" />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
          <InspRow label="Columns">
            <input
              type="number"
              min={1} max={8}
              value={exhibition.gridCols ?? 5}
              onChange={e => upd({ gridCols: Math.max(1, Math.min(8, parseInt(e.target.value) || 5)) })}
              style={{
                width: '100%', border: '1px solid #e4e9f0', borderRadius: 6,
                padding: '6px 10px', fontSize: '12px', outline: 'none',
                fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box',
              }}
            />
          </InspRow>
          <InspRow label="Rows">
            <input
              type="number"
              min={1} max={6}
              value={exhibition.gridRows ?? 3}
              onChange={e => upd({ gridRows: Math.max(1, Math.min(6, parseInt(e.target.value) || 3)) })}
              style={{
                width: '100%', border: '1px solid #e4e9f0', borderRadius: 6,
                padding: '6px 10px', fontSize: '12px', outline: 'none',
                fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box',
              }}
            />
          </InspRow>
        </Box>

        <InspRow label="Gap (px)">
          <input
            type="number"
            min={0} max={80}
            value={exhibition.gridGap ?? 30}
            onChange={e => upd({ gridGap: Math.max(0, Math.min(80, parseInt(e.target.value) || 30)) })}
            style={{
              width: '100%', border: '1px solid #e4e9f0', borderRadius: 6,
              padding: '6px 10px', fontSize: '12px', outline: 'none',
              fontFamily: 'inherit', background: '#f8f9fb', boxSizing: 'border-box',
            }}
          />
        </InspRow>

        <InspRow label="Booth Shape">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {BOOTH_SHAPES.map(s => {
              const active = (exhibition.boothShape || 'trapezoid') === s.value;
              return (
                <Box
                  key={s.value}
                  onClick={() => upd({ boothShape: s.value })}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    borderRadius: '7px', px: 1.25, py: 0.75, cursor: 'pointer',
                    border: `1.5px solid ${active ? EXHIBITION_COLOR : '#e4e9f0'}`,
                    background: active ? alpha(EXHIBITION_COLOR, 0.05) : '#fff',
                    transition: 'all 0.1s',
                  }}
                >
                  <Box sx={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: active ? EXHIBITION_COLOR : '#d4dbe6',
                  }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: active ? 700 : 500, color: active ? EXHIBITION_COLOR : '#3c4555', lineHeight: 1 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.63rem', color: '#9ea8b3' }}>
                      {s.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </InspRow>

        {/* ── Intro Slides ── */}
        <InspSectionTitle label="Intro Modal" color="#9ea8b3" />

        <ToggleRow
          label="Show intro to new visitors"
          value={!!exhibition.showIntro}
          onChange={v => upd({ showIntro: v })}
        />

        {exhibition.showIntro && (
          <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', lineHeight: 1.5, mt: -0.5 }}>
            Intro slides are managed from the Exhibitors section in the admin panel.
          </Typography>
        )}
      </Box>
    </Box>
  );
}